import type {
  LenderExecution,
  PricingMode,
  RateExplanation,
} from "./agent-types";
import type {
  ReadinessCapReason,
  ReadinessSegment,
} from "./readiness-types";

export interface OnboardingData {
  buyerType: string | null;
  targetArea: string | null;
  // place_id of the area suggestion the buyer picked; cleared whenever the
  // text is edited afterwards. Optional so persisted sessions still load.
  targetPlaceId?: string | null;
  householdIncome: number | null;
  employmentMonths: number | null;
  recentJobGap: boolean;
  hasSecondJob: boolean;
  isSelfEmployed: boolean;
  hasBonusIncome: boolean;
  hasCommissionIncome: boolean;
  savings: number | null;
  creditScore: string | null;
  monthlyDebts: number | null;
  budget: number | null;
  monthlyBudget: number | null;
  timeline: string | null;
  housingSituation: string | null;
  leaseExpiration: string | null;
  email: string | null;
  firstName: string | null;
  agentInviteCode: string | null;
  manualInviteCode: string | null;
  agentSharingLevel: AgentSharingLevel | null;
  agentId: string | null;
  agentName: string | null;
  agentAvatar: string | null;
}

export type AgentSharingLevel = "full_financial" | "status_only" | "none";
export type BootstrapStatus = "ready" | "needs_profile_inputs" | "unavailable";
export type LoanProduct =
  | "conventional"
  | "arm_7_6"
  | "fha"
  | "va"
  | "usda"
  | "jumbo";
export type FinancialSourceFamily = "document" | "plaid" | "manual" | "derived";
export type FinancialVerificationTier =
  | "self_reported"
  | "connected"
  | "document"
  | "pre_approval";
export type FinancialFreshnessState = "fresh" | "aging" | "stale";
export type FinancialDerivationKind = "normalized" | "aggregated" | "inferred";
export type FinancialEntityType =
  | "asset_account"
  | "income_stream"
  | "liability_account";
export type FinancialAssetCategory =
  | "cash"
  | "investment"
  | "retirement"
  | "other";

export interface FinancialEntity {
  asset_category?: FinancialAssetCategory;
  asset_subtype?: string;
  id: string;
  type: FinancialEntityType;
}

export interface FinancialField {
  confidence?: string;
  derivation_kind?: FinancialDerivationKind;
  entity?: FinancialEntity;
  field: string;
  freshness_state?: FinancialFreshnessState;
  last_verified_at?: string;
  present: boolean;
  source: string;
  source_as_of?: string;
  source_family?: FinancialSourceFamily;
  source_record_id?: string;
  verification_tier?: FinancialVerificationTier;
}

export type FinancialInputMeta = FinancialField;

export interface FinancialAssumptionMeta {
  field: string;
  profile_name?: string;
  source: string;
  source_as_of?: string;
  value?: unknown;
}

export interface AgentReferralPreview {
  id: string;
  link_id?: string;
  invite_code?: string;
  name: string;
  email?: string;
  /** Shown on the Your Team card when the connected-pros read supplies it. */
  phone?: string;
  role?: string;
  avatar?: string;
  brokerage?: string;
  sharing_level?: AgentSharingLevel;
  share_with_pro?: boolean;
  attribution_failed?: boolean;
  connected: boolean;
  /**
   * How this pro link was created. Present when the backend sets it.
   * `"marketplace"` means the buyer connected through the Marketplace
   * accept flow — triggers the three-step friction dialog on disconnect
   * (M5, plan 255, task:disconnect-marketplace-only). Other values (e.g.
   * `"invite"`) keep the existing simple Your Team disconnect dialog.
   */
  connection_method?: string;
}

export interface AffordabilityEstimate {
  buyingPowerMax: number;
  monthlyPaymentMax: number;
  insight: string;
}

export interface FinancialsRateWatch {
  comparison_products?: FinancialsRateWatchComparisonProduct[];
  current_quote?: FinancialsRateWatchCurrentQuote | null;
  market_history?: FinancialsRateMarketHistoryPoint[];
  personalized_history?: FinancialsRatePersonalizedHistoryPoint[];
}

export interface FinancialsRateWatchCurrentQuote {
  assumptions?: FinancialAssumptionMeta[];
  base_rate_as_of?: string;
  base_rate_percent?: number;
  estimated_payment_monthly?: number;
  fee_lines?: FinancialsRateWatchFeeLine[];
  lender_executions?: LenderExecution[];
  llpa_points?: number;
  location_code?: string;
  missing_inputs?: string[];
  personalized_rate_percent?: number;
  pricing_mode?: PricingMode;
  pricing_mode_note?: string;
  pricing_lines?: FinancialsRateWatchPricingLine[];
  pricing_source_as_of?: string;
  pricing_source_name?: string;
  rate_explanation?: RateExplanation;
  status?: BootstrapStatus;
  target_price?: number;
}

export interface FinancialsRateMarketHistoryPoint {
  base_rate_percent?: number;
  fetched_at?: string;
  observation_date: string;
  pricing_source_name?: string;
}

export interface FinancialsRateWatchBadge {
  slug: "primary" | "ready" | "derived" | "preview";
  label: string;
  tone: "default" | "warning" | "accent";
  tooltip?: string;
}

export interface FinancialsRateWatchComparisonProduct {
  base_rate_percent?: number;
  // Two-layer badge model (SILL-326): `badge` is the catalog-derived
  // static badge (e.g. derived for editorial-only lanes). The composer
  // overlays `primary_overlay=true` on the lane matching the user's
  // active target; the FE picks overlay over base.
  badge: FinancialsRateWatchBadge;
  primary_overlay?: boolean;
  estimated_payment_monthly?: number;
  fee_lines?: FinancialsRateWatchFeeLine[];
  id: string;
  label: string;
  lender_executions?: LenderExecution[];
  llpa_points?: number;
  location_code?: string;
  loan_term_months: number;
  market_history?: FinancialsRateMarketHistoryPoint[];
  personalized_rate_percent?: number;
  pricing_mode?: PricingMode;
  pricing_mode_note?: string;
  pricing_lines?: FinancialsRateWatchPricingLine[];
  pricing_source_as_of?: string;
  pricing_source_name?: string;
  primary?: boolean;
  product: LoanProduct;
  rate_explanation?: RateExplanation;
  short_label: string;
  status: BootstrapStatus;
  target_price?: number;
}

export interface FinancialsRateWatchPricingLine {
  kind: string;
  points?: number;
  rate?: number;
  source?: string;
  source_as_of?: string;
  source_label?: string;
}

export interface FinancialsRateWatchFeeLine {
  amount: number;
  annual_rate?: number;
  financed: boolean;
  kind: string;
  source?: string;
  source_as_of?: string;
  source_label?: string;
  source_url?: string;
}

export interface FinancialsRatePersonalizedHistoryPoint {
  base_rate_percent?: number;
  created_at: string;
  estimated_payment_monthly?: number;
  llpa_points?: number;
  market_observation_date?: string;
  personalized_rate_percent?: number;
  pricing_source_as_of?: string;
  pricing_source_name?: string;
  trigger:
    | "initial"
    | "profile_update"
    | "target_update"
    | "rate_change"
    | "llpa_change";
}

export interface AffordabilityScenario {
  assumptions?: FinancialAssumptionMeta[];
  confidence?: string;
  customer_summary?: string;
  down_payment_needed: number;
  down_payment_percent: number;
  has_pmi: boolean;
  label: string;
  max_dti_ratio: number;
  max_purchase_price: number;
  missing_inputs?: string[];
  monthly_payment: number;
  status?: BootstrapStatus;
}

export interface BackendEngineRates {
  base_rate_percent?: number | null;
  effective_property_tax_rate_percent?: number | null;
  effective_insurance_rate_percent?: number | null;
  monthly_mortgage_insurance_cents?: number | null;
}

export interface FinancialsPayload {
  as_of?: string;
  affordability_scenarios?: AffordabilityScenario[];
  engine_rates?: BackendEngineRates | null;
  inputs?: FinancialField[];
  rate_watch?: FinancialsRateWatch | null;
  status?: BootstrapStatus;
}

export interface OnboardingResponse {
  user: {
    id: string;
    email: string;
    email_verified?: boolean;
    first_name: string;
    phone?: string | null;
    phone_verified?: boolean;
    // role mirrors `users.role` ('consumer' | 'agent' | 'lo' | 'admin').
    // Surfaced so the consumer SPA can gate the /admin/* surface without
    // an extra request. Backend RequireRole(model.RoleAdmin) is still
    // authoritative on every /api/admin/* call.
    role?: string;
    // SILL-173: bootstrap signal for the JourneyRail "ask-sill" step.
    // True when the user has any conversation row (any agent type).
    has_conversation?: boolean;
  };
  profile: {
    id: string;
    current_snapshot_id?: string;
    buyer_type: string;
    credit_bucket?: string;
    credit_score?: string;
    household_income?: number;
    monthly_debts?: number;
    monthly_budget?: number | null;
    reserve_floor_months?: number;
    comfort_dti_pct?: number | null;
    savings?: number;
    target_area: string;
    target_price: number;
    timeline: string;
    housing_situation: string;
    lease_expiration?: string | null;
    employment_months?: number | null;
    recent_job_gap?: boolean;
    has_second_job?: boolean;
    is_self_employed?: boolean;
    has_bonus_income?: boolean;
    has_commission_income?: boolean;
    profile_completeness: number;
    portal_discoverable?: boolean;
    published_at?: string | null;
    publish_requested_at?: string | null;
    // SILL-173: bootstrap signals for the JourneyRail "verify-finances"
    // step. True when at least one document_uploads row / plaid_items
    // row exists for the profile.
    has_uploaded_document?: boolean;
    has_plaid_item?: boolean;
    // Buyer Passport M2 task 677: affordability overlay outcome from the
    // backend's frozen readiness snapshot. Absent/null when the engine
    // produced no buying power (DNQ / pending). The hero renders the
    // estimate-only fallback when these are absent.
    affordability_band_low?: number | null;
    affordability_band_high?: number | null;
    sill_qual_price?: number | null;
    // SILL-537 M5 (vf-shape-editable): the persisted income_shape column,
    // echoed via model.ResolveIncomeShape server-side so a pre-M3 row with
    // no stored shape still echoes its boolean-derived fallback rather
    // than an empty string. Now buyer-editable via PATCH /api/profile's
    // `income_shape` key (see lib/profile/use-profile-edit.ts).
    income_shape?: string;
  };
  readiness: {
    as_of?: string;
    // SILL-323: score is null when a cap override fires. Completion
    // screen renders the missing-state placeholder + cap_reason_detail
    // banner rather than a numeric headline.
    score: number | null;
    label: string;
    description: string;
    buying_power_max: number;
    monthly_payment_max: number;
    insight: string;
    status?: BootstrapStatus;
    segment?: ReadinessSegment;
    cap_reason?: ReadinessCapReason;
    cap_reason_detail?: string;
  };
  financials?: FinancialsPayload | null;
  agent?: AgentReferralPreview | null;
  connected_pros?: AgentReferralPreview[] | null;
  // SILL-311: present when the user has not accepted the current ToS +
  // Privacy versions. The completion screen renders an in-flow acceptance
  // step; LegalAcceptanceGate stays the fallback for stale-version
  // recovery on returning sessions.
  requires_legal_acceptance?: boolean;
  legal?: {
    tos: { url: string; version: string };
    privacy: { url: string; version: string };
  } | null;
}

export interface OnboardingSession {
  data: OnboardingData;
  isComplete: boolean;
  /** Opaque pre-account marketplace share claim; never part of profile input. */
  marketplaceClaimToken?: string | null;
  /** Opaque post-auth referral attribution token; never part of profile input. */
  referralAttributionToken?: string | null;
  /** Fixed public-share completion intent; never caller-controlled navigation. */
  marketplacePublishIntent?: boolean;
  step: number;
}

export const BUYER_TYPES = [
  {
    value: "first-time",
    label: "First-time buyer",
    description: "Buying my first home",
  },
  {
    value: "move-up",
    label: "Move-up buyer",
    description: "Upgrading from my current home",
  },
  {
    value: "downsizing",
    label: "Downsizing",
    description: "Looking for something smaller",
  },
  { value: "investor", label: "Investor", description: "Investment property" },
] as const;

export const CREDIT_SCORE_RANGES = [
  {
    value: "excellent",
    label: "Excellent",
    range: "740+",
    rate: "Best rates available",
  },
  { value: "good", label: "Good", range: "700-739", rate: "Competitive rates" },
  { value: "fair", label: "Fair", range: "660-699", rate: "Standard rates" },
  {
    value: "below",
    label: "Below 660",
    range: "<660",
    rate: "Limited options",
  },
  {
    value: "unsure",
    label: "Not sure",
    range: "",
    rate: "We'll help you check",
  },
] as const;

export const WORK_HISTORY_OPTIONS = [
  {
    value: 24,
    label: "2+ years",
    description: "Cleanest underwriting path for most lenders",
  },
  {
    value: 12,
    label: "1-2 years",
    description: "Usually workable, but 24 months is stronger",
  },
  {
    value: 6,
    label: "6-12 months",
    description: "Likely needs more lender review",
  },
  {
    value: 0,
    label: "Less than 6 months",
    description: "Treat estimates as more directional",
  },
] as const;

export const TIMELINE_OPTIONS = [
  { value: "asap", label: "ASAP", description: "Ready to move now" },
  { value: "1-3months", label: "1-3 months", description: "Active search" },
  { value: "3-6months", label: "3-6 months", description: "Getting prepared" },
  { value: "6-12months", label: "6-12 months", description: "Planning ahead" },
  {
    value: "exploring",
    label: "Just exploring",
    description: "Curious about options",
  },
] as const;

export function getTimelineLabel(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  const matchedOption = TIMELINE_OPTIONS.find(
    (option) => option.value === value,
  );

  if (matchedOption) {
    return matchedOption.label;
  }

  if (value.toLowerCase() === "asap") {
    return "ASAP";
  }

  return value
    .replaceAll("_", " ")
    .replace(/(\d+)-(\d+)([A-Za-z]+)/g, "$1-$2 $3")
    .replace(/(\d+)([A-Za-z]+)/g, "$1 $2")
    .replace(/([A-Za-z])-(?=[A-Za-z])/g, "$1 ");
}

export const HOUSING_SITUATIONS = [
  { value: "renting", label: "Renting", description: "Currently leasing" },
  {
    value: "own-selling",
    label: "Own & selling",
    description: "Selling current home",
  },
  {
    value: "own-keeping",
    label: "Own & keeping",
    description: "Keeping current home",
  },
  {
    value: "with-family",
    label: "Living with family",
    description: "No current lease",
  },
  { value: "other", label: "Other", description: "Different situation" },
] as const;

export function getEmptyOnboardingData(): OnboardingData {
  return {
    buyerType: null,
    targetArea: null,
    householdIncome: null,
    employmentMonths: null,
    recentJobGap: false,
    hasSecondJob: false,
    isSelfEmployed: false,
    hasBonusIncome: false,
    hasCommissionIncome: false,
    savings: null,
    creditScore: null,
    monthlyDebts: null,
    budget: null,
    monthlyBudget: null,
    timeline: null,
    housingSituation: null,
    leaseExpiration: null,
    email: null,
    firstName: null,
    agentInviteCode: null,
    manualInviteCode: null,
    agentSharingLevel: null,
    agentId: null,
    agentName: null,
    agentAvatar: null,
  };
}
